import React, { useEffect } from 'react';
import { PageSEO } from './PageSEO';

interface IndustryPageSEOProps {
  industry: string;
  industrySlug: string;
  description?: string;
  keywords?: string[];
}

export const IndustryPageSEO: React.FC<IndustryPageSEOProps> = ({ 
  industry, 
  industrySlug,
  description,
  keywords = []
}) => {
  const title = `${industry} Financial Advisor | Specialized Wealth Management`;
  const metaDescription = description || `Expert financial planning tailored for ${industry.toLowerCase()}. Tax optimization, investment strategies, and comprehensive wealth management solutions. Free consultation.`;
  
  const defaultKeywords = [
    `${industry} financial advisor`,
    `${industry} wealth management`,
    `${industry} financial planning`,
    `financial advisor for ${industry.toLowerCase()}`,
    `${industry} investment strategies`,
    `${industry} tax planning`,
    `${industry} insurance planning`,
    `${industry} retirement planning`,
    'specialized financial advisor',
    'PCM Financial'
  ];
  
  const allKeywords = [...defaultKeywords, ...keywords];
  
  // Structured data
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": `PCM Financial - ${industry} Services`,
      "description": metaDescription,
      "url": `https://pcmfinancial.ca/${industrySlug}-financial-advisor`,
      "telephone": "1-800-PCM-PLAN",
      "audience": {
        "@type": "Audience",
        "audienceType": industry
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `Financial Services for ${industry}`,
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Professional Corporation Planning",
              "description": "Tax-efficient structures for professional practices"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Income Optimization",
              "description": "Maximize after-tax income through strategic planning"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Risk Management",
              "description": "Comprehensive insurance and liability protection"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Succession Planning",
              "description": "Business and practice transition strategies"
            }
          }
        ]
      },
      "areaServed": {
        "@type": "Country",
        "name": "Canada"
      },
      "priceRange": "$$$$",
      "image": `https://pcmfinancial.ca/assets/industries/${industrySlug}-hero.jpg`,
      "sameAs": [
        "https://www.linkedin.com/company/pcm-financial",
        "https://twitter.com/pcmfinancial"
      ]
    };
    
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pcmfinancial.ca"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Industries",
          "item": "https://pcmfinancial.ca/industries"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": `${industry} Financial Advisor`,
          "item": `https://pcmfinancial.ca/${industrySlug}-financial-advisor`
        }
      ]
    };
    
    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": `What financial services do you offer for ${industry.toLowerCase()}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `We offer comprehensive financial planning services tailored specifically for ${industry.toLowerCase()}, including tax optimization, investment management, insurance planning, retirement strategies, and succession planning.`
          }
        },
        {
          "@type": "Question",
          "name": `How can PCM Financial help ${industry.toLowerCase()} save on taxes?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Through strategic planning including professional corporations, income splitting, tax-deferred investments, and optimized compensation structures, we help ${industry.toLowerCase()} minimize their tax burden while building wealth.`
          }
        },
        {
          "@type": "Question",
          "name": `What makes PCM Financial different for ${industry.toLowerCase()}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `We specialize in the unique financial challenges and opportunities facing ${industry.toLowerCase()}. Our advisors have deep expertise in industry-specific strategies and maintain relationships with other professionals serving your industry.`
          }
        }
      ]
    };
    
    // Add structured data scripts
    const addScript = (data: any, id: string) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
      
      return () => {
        const existingScript = document.getElementById(id);
        if (existingScript) {
          existingScript.remove();
        }
      };
    };
    
    const cleanup1 = addScript(structuredData, `industry-structured-data-${industrySlug}`);
    const cleanup2 = addScript(breadcrumbData, `industry-breadcrumb-data-${industrySlug}`);
    const cleanup3 = addScript(faqData, `industry-faq-data-${industrySlug}`);
    
    return () => {
      cleanup1();
      cleanup2();
      cleanup3();
    };
  }, [industry, industrySlug, metaDescription]);
  
  return (
    <PageSEO
      title={title}
      description={metaDescription}
      keywords={allKeywords}
      image={`/assets/industries/${industrySlug}-hero.jpg`}
      type="website"
    />
  );
};